import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Map from '../components/Map';
import { createGame, getGame, createDailyGame } from '../api/gameApi';
import './Game.css';

function Game() {
  const navigate = useNavigate();
  const [gameState, setGameState] = useState({
    gameId: null,
    phenotypes: [],
    currentRound: 0,
    scores: [],
    isLoading: true,
    error: null,
  });

  const [roundState, setRoundState] = useState({
    guessLocation: null,
    showAnswer: false,
    distance: null,
    score: null,
  });

  const [selectedGender, setSelectedGender] = useState('male');
  const [isCardCollapsed, setIsCardCollapsed] = useState(false);

  const currentPhenotype = gameState.phenotypes[gameState.currentRound];

  useEffect(() => {
    // Check URL for game ID
    const params = new URLSearchParams(window.location.search);
    const gameId = params.get('id');
    const isDailyChallenge = params.get('mode') === 'daily';

    if (gameId) {
      loadExistingGame(gameId);
    } else if (isDailyChallenge) {
      loadDailyGame();
    } else {
      setGameState(prev => ({
        ...prev,
        isLoading: false,
        error: 'No game ID provided',
      }));
    }
  }, []);

  async function startNewGame() {
    try {
      const data = await createGame();
      setGameState({
        gameId: data.gameId,
        phenotypes: data.phenotypes,
        currentRound: 0,
        scores: [],
        isLoading: false,
        error: null,
      });
    } catch (err) {
      setGameState(prev => ({
        ...prev,
        isLoading: false,
        error: err.message,
      }));
    }
  }

  async function loadExistingGame(gameId) {
    try {
      const data = await getGame(gameId);
      setGameState({
        gameId: data.gameId,
        phenotypes: data.phenotypes,
        currentRound: 0,
        scores: [],
        isLoading: false,
        error: null,
      });
    } catch (err) {
      setGameState(prev => ({
        ...prev,
        isLoading: false,
        error: err.message,
      }));
    }
  }

  async function loadDailyGame() {
    try {
      // This endpoint is idempotent: it creates today's game if needed and
      // otherwise returns the existing one.
      const dailyGame = await createDailyGame();
      await loadExistingGame(dailyGame.gameId);
    } catch (err) {
      setGameState(prev => ({
        ...prev,
        isLoading: false,
        error: err.message,
      }));
    }
  }

  function handleMapClick(latlng) {
    if (!roundState.showAnswer) {
      setRoundState(prev => ({
        ...prev,
        guessLocation: latlng,
      }));
    }
  }

  function calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371; // Earth radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  function calculateScore(distance) {
    if (distance > 2000) return 0;
    return Math.round(5000 * Math.exp(-distance / 1000));
  }

  async function submitGuess() {
    if (!roundState.guessLocation || !currentPhenotype) return;

    // Load GeoJSON to get centroid
    const geojsonPath = `/assets/maps/${currentPhenotype.id}.geojson`;
    const response = await fetch(geojsonPath);
    const geojson = await response.json();

    // Calculate centroid (simple average for now)
    let totalLat = 0, totalLng = 0, count = 0;
    geojson.features.forEach(feature => {
      if (feature.geometry.type === 'Polygon') {
        feature.geometry.coordinates[0].forEach(coord => {
          totalLng += coord[0];
          totalLat += coord[1];
          count++;
        });
      }
    });
    const centroidLat = totalLat / count;
    const centroidLng = totalLng / count;

    const distanceKm = calculateDistance(
      roundState.guessLocation.lat,
      roundState.guessLocation.lng,
      centroidLat,
      centroidLng
    );

    const distance = Math.round(distanceKm);
    const score = calculateScore(distanceKm);

    setRoundState({
      guessLocation: roundState.guessLocation,
      showAnswer: true,
      distance: distance,
      score: score,
    });

    setGameState(prev => ({
      ...prev,
      scores: [...prev.scores, { score, distance }],
    }));
  }

  function nextRound() {
    if (gameState.currentRound < gameState.phenotypes.length - 1) {
      setGameState(prev => ({
        ...prev,
        currentRound: prev.currentRound + 1,
      }));
      setRoundState({
        guessLocation: null,
        showAnswer: false,
        distance: null,
        score: null,
      });
    } else {
      // Game finished - navigate to summary
      // Score already added to gameState.scores in submitGuess
      const scoresData = encodeURIComponent(JSON.stringify(gameState.scores));
      window.location.href = `/summary?id=${gameState.gameId}&scores=${scoresData}`;
    }
  }

  if (gameState.isLoading) {
    return <div className="loading">Loading game...</div>;
  }

  if (gameState.error) {
    return <div className="error">Error: {gameState.error}</div>;
  }

  if (!currentPhenotype) {
    return <div className="error">No phenotype data</div>;
  }

  return (
    <div className="game-container">
      <div className="game-content">
        <div className="map-panel">
          <Map
            geojsonPath={`/assets/maps/${currentPhenotype.id}.geojson`}
            onMapClick={handleMapClick}
            guessLocation={roundState.guessLocation}
            showAnswer={roundState.showAnswer}
          />

          <button
            className="back-to-home-button"
            onClick={() => navigate('/')}
            aria-label="Back to Home"
          >
            ← Home
          </button>

          <div className={`phenotype-card ${isCardCollapsed ? 'collapsed' : ''}`}>
            <div className="card-header">
              <div className="round-info">
                Round {gameState.currentRound + 1} / {gameState.phenotypes.length}
              </div>
              <button
                className="collapse-button"
                onClick={() => setIsCardCollapsed(!isCardCollapsed)}
                aria-label={isCardCollapsed ? 'Expand' : 'Collapse'}
              >
                {isCardCollapsed ? '▼' : '▲'}
              </button>
            </div>
            {!isCardCollapsed && (
              <div className="images-row">
                <div className="image-box">
                  <img
                    src={`/assets/images/${currentPhenotype.id}_male.jpg`}
                    alt="Male"
                    className="phenotype-image"
                  />
                  <span className="image-label">Male</span>
                </div>
                <div className="image-box">
                  <img
                    src={`/assets/images/${currentPhenotype.id}_female.jpg`}
                    alt="Female"
                    className="phenotype-image"
                  />
                  <span className="image-label">Female</span>
                </div>
              </div>
            )}
          </div>

          {!roundState.showAnswer && !roundState.guessLocation && (
            <div className="instruction-overlay">
              Click on the map to make your guess
            </div>
          )}

          {!roundState.showAnswer && roundState.guessLocation && (
            <div className="submit-overlay">
              <button onClick={submitGuess} className="submit-button">
                Submit Guess
              </button>
            </div>
          )}

          {roundState.showAnswer && (
            <div className="result-overlay">
              <div className="result-panel">
                <div className="result-item">
                  <span className="result-label">Distance:</span>
                  <span className="result-value">{roundState.distance} km</span>
                </div>
                <div className="result-item">
                  <span className="result-label">Score:</span>
                  <span className="result-value">{roundState.score}</span>
                </div>
                <div className="result-item">
                  <span className="result-label">Location:</span>
                  <span className="result-value">{currentPhenotype.name}</span>
                </div>
                {currentPhenotype.description && (
                  <div className="description-box">
                    <p>{currentPhenotype.description}</p>
                  </div>
                )}
                <button onClick={nextRound} className="next-button">
                  {gameState.currentRound < gameState.phenotypes.length - 1
                    ? 'Next Phenotype →'
                    : 'View Summary 🏆'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Game;
